import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const basicInterpolationCode = `// Architectural Logic: String Interpolation
// Unlike concatenation, template literals allow for complex expression embedding
const user = { name: 'Nikola', role: 'Architect' };
const loginCount = 42;

const welcomeMessage = \`Welcome back \${user.name}. 
Current status: \${user.role}. 
Action required: \${loginCount &gt; 10 ? 'Audit Logs' : 'None'}.\`;`;

const taggedTemplateCode = `// Engineering Pattern: Tagged Templates
// Standard Tag function signature: (StringsArray, ...InterpolatedValues)
function highlight(strings, ...values) {
    return strings.reduce((acc, part, i) => {
        const val = values[i] ? \`<span class="hl">\${values[i]}</span>\` : '';
        return acc + part + val;
    }, '');
}

const product = 'Vite';
const version = '5.0';

// The tag 'highlight' intercepts the template before evaluation
const output = highlight\`Using \${product} version \${version}.\`;
// Result: Using <span class="hl">Vite</span> version <span class="hl">5.0</span>.`;

const dslStyledCode = `// Practical DSL: Styled-Components Pattern
// Creating a mini-language for CSS inside JavaScript
const styled = (strings, ...exprs) => (props) => {
    return strings.reduce((acc, str, i) => {
        // Resolve expressions if they are functions (passing props)
        const val = typeof exprs[i] === 'function' ? exprs[i](props) : exprs[i];
        return acc + str + (val || '');
    }, '');
};

const ButtonStyle = styled\`
  color: \${props => props.active ? 'cyan' : 'gray'};
  font-weight: bold;
\`;

console.log(ButtonStyle({ active: true })); // color: cyan; font-weight: bold;`;

const JSTemplateLiterals = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">String Engineering</div>
                <h1>Template Literals & Tagged Templates</h1>
                <p className="hero-lead">
                    Beyond simple strings: Master the architectural power of Template Literals.
                    Learn to build Domain-Specific Languages (DSLs), secure HTML sanitizers,
                    and performant string templates for modern engineering.
                </p>
            </div>

            <div className="content-section">
                <h2>The Mechanics of Interpolation</h2>
                <p>
                    Template literals (using backticks ``) provide a cleaner syntax for string
                    creation. More importantly, they allow for **Expression Embedding**.
                    Inside `${"{ }"}`, you can run any valid JavaScript: ternary operators,
                    function calls, or complex mathematical operations.
                </p>
                <CodeBlock code={basicInterpolationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Advanced: Tagged Templates</h2>
                <p>
                    A tagged template is an expression where a function "tags" a template
                    literal. This allows you to intercept and process the string fragments
                    and interpolated values *before* they are combined into a final string.
                </p>
                <CodeBlock code={taggedTemplateCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Building a DSL (Domain-Specific Language)</h2>
                <p>
                    Libraries like `styled-components` and `lit-html` leverage tagged
                    templates to create DSLs within JavaScript. By passing a function
                    as an expression, you can create dynamic systems that react to
                    application state (props).
                </p>
                <CodeBlock code={dslStyledCode} language="javascript" />
            </div>

            <ProTip><p>
                    JavaScript engines (like V8) optimize template literals. Static parts of
                    a template are often "interned" (stored once in memory), making
                    repeated evaluations of the same static template very efficient compared
                    to manual string concatenation.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Security:** Use tagged templates to automatically escape HTML (XSS prevention).</li>
                    <li>**Readability:** Replace all multi-line strings (" + \n) with backticks.</li>
                    <li>**SQL:** Tagged templates are ideal for building safe, parameterized SQL queries.</li>
                    <li>**Translation:** Use tags for i18n (Internationalization) lookups.</li>
                    <li>**Raw Strings:** Use `String.raw` to get the unescaped backslash version of a template.</li>
                    <li>**Logic:** Keep logic inside `${"{ }"}` simple; if it exceeds 3 lines, move it to a helper function.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSTemplateLiterals;





